import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Item } from '@/types/item';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemList, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import { transformDateString } from '@/utilities/datetime';
import ModalView from '@/components/modals/Modal';
import { getExtraPayoutUrl } from '@/utilities/url';

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.seller_payment.actions.release_payment'),
      onClick: () => setModal({ kind: 'ItemReleasePayment', item, onClose: () => setModal(null) }),
    },
    {
      kind: 'Button',
      label: t('item.seller_payment.actions.proof_of_payment'),
      onClick: () => setModal({ kind: 'ItemProofOfPayment', item, onClose: () => setModal(null) }),
    },
    {
      kind: 'Button',
      label: t('client.extra_payouts.create').toUpperCase(),
      onClick: () => {
        window.open(getExtraPayoutUrl({ action: 'create', productId: item?.productId }), '_blank');
      },
    },
    {
      kind: 'Button',
      label: t('item.seller_payment.actions.cancel_early_payout'),
      onClick: () => setModal({ kind: 'ItemCancelEarlyPayout', item, onClose: () => setModal(null) }),
    },
  ];

  const items = [
    itemText(t('item.seller_payment.seller_expected_amount'), item.netSellingPrice),
    itemText(t('item.seller_payment.date_of_last_status'), item.productSellerPayout.lastStatusDate),
    itemText(t('item.seller_payment.item_amount'), item.productSellerPayout.userCurrencyAmount),
    itemText(t('item.seller_payment.item_deposit_currency'), item.productSellerPayout.userPayoutCurrency),
    itemText(
      t('item.seller_payment.estimated_seller_payment_date'),
      transformDateString(item.estimatedSellerPaymentDate),
    ),
    itemText(t('item.seller_payment.account_type'), item.productSellerPayout.accountType),
    itemText(t('item.seller_payment.bank_name'), item.productSellerPayout.bankName),
    itemText(t('item.seller_payment.account_detail'), item.productSellerPayout.accountDetail),
    itemText(t('item.seller_payment.transfer_blocked'), item.isTransferBlocked),
    itemText(t('item.seller_payment.blocked_by_litigation'), item.isBlockedByLitigation),
    itemText(
      t('item.seller_payment.date_of_seller_transfer'),
      item.sellerTransferDade === '0000-00-00 00:00:00' ? 'N/A' : transformDateString(item.sellerTransferDade),
    ),
    itemList(
      t('item.seller_payment.split_payment_status'),
      item.productSellerPayout.splitPaymentStatuses?.length ? item.productSellerPayout.splitPaymentStatuses : null,
      {
        kind: 'Button',
        label: t('common.details').toUpperCase(),
        onClick: () => setModal({ kind: 'PayoutStatusDetail', item, onClose: () => setModal(null) }),
        disabled: !item.productSellerPayout.payoutId,
      },
    ),
    itemText(t('item.seller_payment.fail_reason'), item.productSellerPayout.failedReason),
    itemText(t('item.seller_payment.internal_payout_status'), item.productSellerPayout.internalPayoutStatus),
  ];

  return (
    <>
      <Container
        title={t('item.seller_payment.title')}
        actions={actions}
        testId="sller_payment"
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
